import { Track } from "@/src/lib/tracking";
import { useCallback, useEffect, useState } from "react";

import { Icon } from "@ospweb/sdk-ui";
import { ChatStructuredCardShell } from "../StructuredCards";
import Markdown from "react-markdown";
import NextImage from "../../NextImage";
import { ViewMore } from "./ViewMore";
import { buildTrackerSignalTrackedOnLabel } from "./trackerSignalMetaLabel";
import { cn } from "utils";
import { formatNotificationGeneratedTime } from "./utils";
import { markNotifyMessageAsRead } from "@components/Notification/localNotificationState";
import { rootStore } from "@store/rootStore";
import { useJsonRenderRuntimeContext } from "../jsonRender/JsonRenderRuntimeContext";
import { useSnapshot } from "valtio";
import type { NotificationCardSceneContext } from "./type";

const stripEmojiFromInvestTrackerTitle = (value: string) =>
  value
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();

export interface NotificationCardContainerProps {
  label: string;
  title: string;
  summary?: string;
  generatedAtMs: number;
  className?: string;
  /** Client-enriched scene params (runtime context may override spec props). */
  sceneContext?: NotificationCardSceneContext;
  children?: React.ReactNode;
}

/**
 * 说明：通知卡外层容器，承载标题、子组件内容和底部操作按钮。
 * Figma：Chat 81033:18324 · Tracker History Sheet 87676:37795。
 * @param param0
 * @returns
 */
export const NotificationCardContainer: React.FC<
  NotificationCardContainerProps
> = ({
  label,
  generatedAtMs,
  title,
  className,
  summary,
  sceneContext,
  children,
}: NotificationCardContainerProps) => {
  const renderContext = useJsonRenderRuntimeContext();
  const scene = renderContext?.scene ?? "notification";
  const collapsed = renderContext?.collapsed ?? false;
  const isRead = renderContext?.isRead ?? false;
  const notificationId = renderContext?.notificationId;
  const isChatScene = scene === "chat";
  const isTrackerScene = scene === "tracker";
  const isTrackerHistoryScene = scene === "tracker-history";
  const isInvestNextMovesScene = scene === "invest-nextmoves";
  const isNotificationScene = scene === "notification";
  const effectiveSceneContext = renderContext?.sceneContext ?? sceneContext;
  const trackerTrackedAtMs = renderContext?.trackedAtMs ?? generatedAtMs;
  const trackerMetaLabel = isTrackerScene
    ? buildTrackerSignalTrackedOnLabel(
        trackerTrackedAtMs,
        renderContext?.locale,
      )
    : label;
  const showTrackerMeta = isTrackerScene && !renderContext?.suppressTrackerMeta;
  const rawInvestMovesLabel =
    effectiveSceneContext?.trackerTitle?.trim() || label;
  const investMovesLabel =
    stripEmojiFromInvestTrackerTitle(rawInvestMovesLabel);
  const investMovesLabelIconName = effectiveSceneContext?.goalId?.trim()
    ? "goal"
    : "signal-circle";
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  const [localIsRead, setLocalIsRead] = useState(isRead);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const effectiveIsRead = localIsRead || isRead;

  useEffect(() => {
    setLocalIsRead(isRead);
  }, [isRead]);

  useEffect(() => {
    // 曝光走 `Track.view`（`event_type=view`），不再裸调 `emitOpenReplayEvent` ——
    // 绕过门面就没有 envelope（`event_type`/`scene`/`app_run_id`/`seq`/`container`），
    // 后台按字段筛不出来、串不进链路，下游只能靠事件名识别。规范 §4.3「门面内部不许绕过自己」。
    //
    // ⚠️ 业务键 `scene` 必须改名 `card_scene`：envelope 自己会注入一个 `scene`
    //（当前路由派生的 Scene 枚举），而 `envelope()` 是先 spread 业务参数再写 envelope 键，
    // 同名的业务值会被**静默覆盖**。下游查询用的就是这个字段，改名已同步知会。
    // 两端同步：native `ChatNotificationCards.kt` 同一天改成了同样的键名。
    Track.view("chat_card_view", {
      id: notificationId,
      title,
      summary,
      generatedAtMs,
      card_scene: scene,
    });
  }, [notificationId, title, summary, generatedAtMs, scene]);

  const handleToggleCollapse = useCallback(() => {
    setLocalIsRead(true);
    if (isNotificationScene) {
      markNotifyMessageAsRead(ownerInfo?.id, notificationId, generatedAtMs);
    }
    Track.event("bell_signal_tap", {
      content_scene: scene,
    });
    setIsCollapsed((prev) => !prev);
  }, [
    generatedAtMs,
    isNotificationScene,
    notificationId,
    ownerInfo?.id,
    scene,
  ]);

  return (
    <ChatStructuredCardShell
      className={cn(
        "flex w-full flex-col",
        isChatScene &&
          "flex-row gap-2 rounded-3 border border-border_white_transparent10 bg-background_solid_primary md:max-w-[500px]",
        isTrackerScene &&
          "gap-3 rounded-[14px] !border-0 !bg-background_solid_neutral_primary !p-4 !shadow-none",
        isInvestNextMovesScene &&
          "gap-4 rounded-xl !border-0 !bg-background_solid_neutral_primary !p-4 !shadow-none",
        isNotificationScene && "flex-row gap-2",
        cn(
          "!border-0 !shadow-none",
          renderContext?.suppressTrackerMeta
            ? "!mt-0 !rounded-none !bg-transparent !p-0 !border-b-0"
            : "gap-3 rounded-[14px] !bg-background_solid_neutral_primary !p-4",
        ),

        !isChatScene &&
          !isTrackerScene &&
          !isInvestNextMovesScene &&
          !effectiveIsRead &&
          "bg-background_brand_overlay5",
        !isChatScene && "border-b border-border_white_transparent5",
        className,
      )}
      data-tracking-eventname="bell_signal_view"
      data-tracking-event-key-content_scene={scene}
    >
      {isNotificationScene && (
        <NextImage
          src="/assets/images/unread_dot.svg"
          className={cn(
            "w-1.5 h-1.5 mt-[31px]",
            !effectiveIsRead ? "visible" : "invisible",
          )}
          width={6}
          height={6}
        />
      )}

      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col",
          isInvestNextMovesScene ? "gap-4" : "gap-3",
        )}
        data-message-id={notificationId}
      >
        <div
          className={cn(
            "flex flex-col",
            isTrackerScene || isInvestNextMovesScene ? "gap-3" : "gap-2",
            isCollapsed &&
              !isTrackerScene &&
              !isInvestNextMovesScene &&
              "cursor-pointer",
          )}
          onClick={
            isCollapsed && !isTrackerScene && !isInvestNextMovesScene
              ? handleToggleCollapse
              : undefined
          }
        >
          {isTrackerScene ? (
            <>
              {showTrackerMeta ? (
                <span
                  className="body_medium_s text-content_tertiary"
                  data-testid="desk-detail-history-drawer-tracked-meta-view"
                >
                  {trackerMetaLabel}
                </span>
              ) : null}
              {/* modify markdown */}
              <Markdown className="title_xl text-content_primary">
                {title}
              </Markdown>

              {summary ? (
                <span className="body_m text-content_primary">{summary}</span>
              ) : null}
            </>
          ) : isInvestNextMovesScene ? (
            <div className="flex flex-col gap-1">
              <div className="flex min-w-0 items-start gap-1">
                <Icon
                  name={investMovesLabelIconName}
                  size={12}
                  className="mt-0.5 shrink-0 text-content_quaternary"
                  aria-hidden
                />
                <span className="body_s min-w-0 flex-1 line-clamp-2 text-content_quaternary">
                  {investMovesLabel}
                </span>
              </div>
              <Markdown className="title_xl line-clamp-3 text-content_primary [&_p]:!mb-0 [&_p]:!mt-0 [&_p]:line-clamp-3 [&_p]:title_xl [&_p]:text-content_primary">
                {title}
              </Markdown>
            </div>
          ) : (
            <>
              <div className="flex flex-row items-center justify-between">
                <span className="body_medium_s text-content_tertiary">
                  {label}
                </span>
                {!isChatScene && (
                  <span className="body_medium_s text-content_quaternary">
                    {formatNotificationGeneratedTime(generatedAtMs)}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                {/* modify markdown */}
                <Markdown className="body_bold_m text-content_primary">
                  {title}
                </Markdown>
                {summary ? (
                  <span
                    className={cn("body_m text-content_secondary", {
                      "line-clamp-2 overflow-hidden text-ellipsis": isCollapsed,
                    })}
                  >
                    {summary}
                  </span>
                ) : null}
              </div>
            </>
          )}
        </div>
        {isCollapsed ? (
          <ViewMore
            onClick={handleToggleCollapse}
            openReplayScene={
              scene === "chat"
                ? "Chat"
                : scene === "tracker"
                  ? "Tracker"
                  : "Notification"
            }
          />
        ) : (
          <div
            className={cn(
              (isTrackerScene ||
                isTrackerHistoryScene ||
                isInvestNextMovesScene) &&
                "flex flex-col gap-4",
            )}
          >
            {children}
          </div>
        )}
      </div>
    </ChatStructuredCardShell>
  );
};
